/**
 * Form Validation Utilities
 * Contains validation functions for contact form fields
 */

/**
 * Email validation using regex
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Phone validation (Korean format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePhone = (phone) => {
  // Allow empty phone (optional field)
  if (!phone || phone.trim() === '') return true;

  // Korean phone formats: 010-1234-5678, 02-1234-5678, +82-10-1234-5678
  const phoneRegex = /^(\+82-?)?0?\d{1,4}-?\d{3,4}-?\d{4}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Required field validation
 * @param {string} value - Value to validate
 * @returns {boolean} - True if not empty, false otherwise
 */
export const validateRequired = (value) => {
  return value !== null && value !== undefined && value.trim() !== '';
};

/**
 * Validate entire contact form
 * @param {object} formData - Form data object
 * @param {object} language - Current language ('ko' or 'en')
 * @returns {object} - Errors object with field names as keys
 */
export const validateContactForm = (formData, language = 'ko') => {
  const errors = {};

  // Name validation
  if (!validateRequired(formData.name)) {
    errors.name = language === 'ko' ? '이름을 입력해주세요' : 'Please enter your name';
  }

  // Email validation
  if (!validateRequired(formData.email)) {
    errors.email = language === 'ko' ? '이메일을 입력해주세요' : 'Please enter your email';
  } else if (!validateEmail(formData.email)) {
    errors.email = language === 'ko' ? '올바른 이메일 형식이 아닙니다' : 'Invalid email format';
  }

  // Phone validation (optional but validate format if provided)
  if (formData.phone && !validatePhone(formData.phone)) {
    errors.phone = language === 'ko' ? '올바른 전화번호 형식이 아닙니다' : 'Invalid phone format';
  }

  // Inquiry type validation
  if (!validateRequired(formData.inquiryType)) {
    errors.inquiryType = language === 'ko' ? '문의 유형을 선택해주세요' : 'Please select inquiry type';
  }

  // Message validation
  if (!validateRequired(formData.message)) {
    errors.message = language === 'ko' ? '문의 내용을 입력해주세요' : 'Please enter your message';
  } else if (formData.message.trim().length < 10) {
    errors.message = language === 'ko' ? '문의 내용을 10자 이상 입력해주세요' : 'Message must be at least 10 characters';
  }

  // Privacy checkbox validation
  if (!formData.privacy) {
    errors.privacy = language === 'ko' ? '개인정보 처리방침에 동의해주세요' : 'Please agree to privacy policy';
  }

  return errors;
};
